// Thin wrapper around driver.js — runs one tour module's steps as a spotlight
// walkthrough. Steps whose anchor isn't in the DOM are dropped at runtime, so
// conditional UI (flag-gated 1040 import, FIA select) never strands the tour.

import { driver, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './tour.css';
import type { TourStep } from './content';

/**
 * True when the event target sits inside a walkthrough popover. Radix sheets and
 * dialogs treat a pointer-down outside their content as a dismiss; the popover is
 * appended to <body>, so without this guard clicking Next would close the very
 * panel the tour is explaining.
 */
export function isTourPopover(target: EventTarget | null): boolean {
  return target instanceof Element && !!target.closest('.driver-popover');
}

/**
 * How every tour behaves (Drew, 2026-09-30: "if you click and type, it goes
 * away; if you click All clients, it goes away"). A click on the dark overlay
 * does nothing — only the X, Back, Next and Got it move the tour — and the
 * spotlighted thing is look-only unless the step opts in (`interactive`),
 * which only the steps that continue from that click do.
 */
export const TOUR_OPTIONS = {
  nextBtnText: 'Next',
  prevBtnText: 'Back',
  doneBtnText: 'Got it',
  overlayOpacity: 0.6,
  stagePadding: 6,
  allowClose: true,
  overlayClickBehavior: () => {},
  disableActiveInteraction: true,
  // Scroll to the next stop instead of jumping there (Drew, 2026-10-06: "it
  // will jump down the page to show the new one instead of slowly scrolling").
  smoothScroll: true,
} as const;

/** The driver.js steps for the tour steps whose anchors are on screen. */
export function driverStepsFor(steps: TourStep[]) {
  return steps.map(s => ({
    element: s.target,
    popover: { title: s.title, description: s.body },
    disableActiveInteraction: !s.interactive,
  }));
}

/**
 * Start a tour over the given steps. Returns the driver instance (so the
 * caller can destroy it on navigation), or null if no step has a visible
 * anchor. `onDone` fires exactly once when the tour ends for any reason —
 * finished, closed early, or destroyed by navigation.
 */
export function runTour(steps: TourStep[], onDone: () => void): Driver | null {
  const present = steps.filter(s => !s.target || document.querySelector(s.target));
  if (present.length === 0) {
    onDone();
    return null;
  }

  let done = false;
  const finish = () => {
    if (done) return;
    done = true;
    onDone();
  };

  const instance = driver({
    ...TOUR_OPTIONS,
    showProgress: present.length > 1,
    steps: driverStepsFor(present),
    onDestroyed: finish,
  });

  instance.drive();
  return instance;
}
